import {
  ObservableParameterModel,
  ObservableProcedureModel,
} from '@blockly/block-shareable-procedures';
import {installAllBlocks as installFieldColourBlocks} from '@blockly/field-colour';
import {CrossTabCopyPaste} from '@blockly/plugin-cross-tab-copy-paste';
import {ScrollOptions} from '@blockly/plugin-scroll-options';
import * as BlocklyCore from 'blockly/core';
import {javascriptGenerator} from 'blockly/javascript';

import {
  READ_ONLY_PROPERTIES,
  SETTABLE_PROPERTIES,
  WORKSPACE_EVENTS,
} from '@cdo/apps/blockly/constants';
import {MetricEvent} from '@cdo/apps/metrics/events';
import {getStore} from '@cdo/apps/redux';
import {setFailedToGenerateCode} from '@cdo/apps/redux/blockly';
import styleConstants from '@cdo/apps/styleConstants';
import * as utils from '@cdo/apps/utils';

import {START_BLOCKS} from '../constants';
import {START_SOURCES} from '../lab2/constants';

import CdoBlockSerializer from './addons/cdoBlockSerializer';
import CdoConnectionChecker from './addons/cdoConnectionChecker';
import initializeCdoConstants from './addons/cdoConstants';
import initializeCss from './addons/cdoCss';
import CdoFieldColour from './addons/cdoFieldColour';
import CdoFieldDropdown from './addons/cdoFieldDropdown';
import CdoFieldLabel from './addons/cdoFieldLabel';
import CdoFieldNumber from './addons/cdoFieldNumber';
import CdoFieldParameter from './addons/cdoFieldParameter';
import CdoFieldVariable from './addons/cdoFieldVariable';
import initializeGenerator from './addons/cdoGenerator';
import {gestureOverrides} from './addons/cdoGesture';
import CdoMetricsManager from './addons/cdoMetricsManager';
import CdoRendererGeras from './addons/cdoRendererGeras';
import CdoRendererThrasos from './addons/cdoRendererThrasos';
import CdoRendererZelos from './addons/cdoRendererZelos';
import {initializeScrollbarPair} from './addons/cdoScrollbar';
import {CdoScrollBlockDragger} from './addons/cdoScrollBlockDragger';
import {getPointerBlockImageUrl} from './addons/cdoSpritePointer';
import CdoTrashcan from './addons/cdoTrashcan';
import initializeVariables from './addons/cdoVariables';
import CdoVerticalFlyout from './addons/cdoVerticalFlyout';
import initializeBlocklyXml, {
  removeInvisibleBlocks,
  removeStaticCallBlocks,
} from './addons/cdoXml';
import {registerAllContextMenuItems} from './addons/contextMenu';
import registerLogicCompareMutator from './addons/extensions/logic_compare';
import FunctionEditor from './addons/functionEditor';
import {filterFunctionArgVariables} from './addons/plusMinusBlocks/advancedProcedures';
import registerIfMutator from './addons/plusMinusBlocks/if';
import registerTextJoinMutator from './addons/plusMinusBlocks/text_join';
import {UNKNOWN_BLOCK} from './addons/unknownBlock';
import {Renderers, Themes} from './constants';
import {flyoutCategory as behaviorsFlyoutCategory} from './customBlocks/behaviorBlocks';
import {flyoutCategory as functionsFlyoutCategory} from './customBlocks/proceduresBlocks';
import {flyoutCategory as variablesFlyoutCategory} from './customBlocks/variableBlocks';
import {
  adjustCalloutsOnViewportChange,
  bumpRTLBlocks,
  disableOrphans,
  handleGrayUndeletableBlocks,
  reflowToolbox,
  setPathFill,
  storeWorkspaceWidth,
  updateBlockLimits,
} from './eventHandlers';
import {
  CdoDeuteranopiaDarkTheme,
  CdoProtanopiaDarkTheme,
  CdoTritanopiaDarkTheme,
} from './themes/cdoAccessibleDarkThemes';
import {
  CdoDeuteranopiaTheme,
  CdoProtanopiaTheme,
  CdoTritanopiaTheme,
} from './themes/cdoAccessibleThemes';
import CdoDarkTheme from './themes/cdoDark';
import CdoHighContrastTheme from './themes/cdoHighContrast';
import CdoHighContrastDarkTheme from './themes/cdoHighContrastDark';
import CdoJigsawTheme from './themes/cdoJigsaw';
import CdoTheme from './themes/cdoTheme';
import {
  BlocklyCoreInstance,
  BlocklyWrapperType,
  ExtendedBlocklyOptions,
  ExtendedJavascriptGenerator,
  ExtendedWorkspace,
  ExtendedWorkspaceSvg,
} from './types';
import {
  cleanUp,
  createBlockLimitMap,
  getUserTheme,
  handleCodeGenerationFailure,
  initializeVariableLocalization,
  isDarkTheme,
  loadBlocksToWorkspace,
  setThemeAndRenderBlocks,
  strip,
} from './utils';

const options: {contextMenu: true; shortcut: true} = {
  contextMenu: true,
  shortcut: true,
};

const crossTabCopyPastePlugin = new CrossTabCopyPaste();
crossTabCopyPastePlugin.init(options);

const MAX_GET_CODE_RETRIES = 2;
const RETRY_GET_CODE_INTERVAL_MS = 500;

/**
 * Wrapper class for https://github.com/RaspberryPiFoundation/blockly
 * This wrapper was used to facilitate migrating from CDO Blockly to
 * mainline (then Google) Blockly by allowing us to unify the APIs so
 * that we can switch out the underlying Blockly object without affecting
 * apps code.
 * This wrapper now contains most of our customizations to Blockly.
 */
const BlocklyWrapper = function (
  this: BlocklyWrapperType,
  blocklyInstance: BlocklyCoreInstance
) {
  this.blockly_ = blocklyInstance;
  this.mainWorkspace = undefined;
  this.embeddedWorkspaces = [];

  this.wrapReadOnlyProperty = function (propertyName) {
    Object.defineProperty(this, propertyName, {
      get: function () {
        return this.blockly_[propertyName];
      },
    });
  };

  this.wrapSettableProperty = function (propertyName) {
    Object.defineProperty(this, propertyName, {
      get: function () {
        return this.blockly_[propertyName];
      },
      set: function (newValue) {
        this.blockly_[propertyName] = newValue;
      },
    });
  };
};

// Monkey patch: prevents toolbox from consuming keydown events so they reach
// the workspace keyboard navigation handler. Still needed per blockly issue #713.
// Also defers onClick_ past any pending focus/blur callbacks — Blockly 13's
// FocusManager locks itself during those callbacks, which causes onClick_ to
// throw when clicking the toolbox immediately after workspace disposal.
class NavigationDeferringToolbox extends BlocklyCore.Toolbox {
  protected override onKeyDown_(e: KeyboardEvent) {}

  protected override onClick_(e: PointerEvent) {
    try {
      super.onClick_(e);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('FocusManager state changes cannot happen')
      ) {
        setTimeout(() => super.onClick_(e), 0);
      } else {
        throw error;
      }
    }
  }
}

function initializeBlocklyWrapper(blocklyInstance: BlocklyCoreInstance) {
  registerIfMutator();
  registerLogicCompareMutator();
  registerTextJoinMutator();
  BlocklyCore.registry.register(
    BlocklyCore.registry.Type.TOOLBOX,
    BlocklyCore.registry.DEFAULT,
    NavigationDeferringToolbox,
    true
  );
  // TODO: can we avoid using any here by converting BlocklyWrapper to a class?
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const blocklyWrapper = new (BlocklyWrapper as any)(
    blocklyInstance
  ) as BlocklyWrapperType;

  blocklyWrapper.getWorkspaceCode = function () {
    return getWorkspaceCodeHelper(0, this.getHiddenDefinitionWorkspace());
  };

  const getWorkspaceCodeHelper = (
    retryCount: number,
    hiddenWorkspace: BlocklyCore.Workspace | undefined
  ): string => {
    let workspaceCode = '';
    try {
      workspaceCode = Blockly.JavaScript.workspaceToCode(
        Blockly.mainBlockSpace
      );
      if (hiddenWorkspace) {
        workspaceCode += Blockly.JavaScript.workspaceToCode(hiddenWorkspace);
      }
      workspaceCode = strip(workspaceCode);
      getStore().dispatch(setFailedToGenerateCode(false));
    } catch (e) {
      if (retryCount < MAX_GET_CODE_RETRIES) {
        // Sometimes we need to wait for Blockly change handlers to complete
        // before the code will generate correctly. Retry after a short delay.
        setTimeout(() => {
          return getWorkspaceCodeHelper(retryCount + 1, hiddenWorkspace);
        }, RETRY_GET_CODE_INTERVAL_MS);
      } else {
        handleCodeGenerationFailure(
          MetricEvent.GOOGLE_BLOCKLY_GET_CODE_ERROR,
          e as Error
        );
      }
    }
    return workspaceCode;
  };

  READ_ONLY_PROPERTIES.forEach(prop => {
    blocklyWrapper.wrapReadOnlyProperty(prop);
  });

  // Installs colour_picker, colour_rgb, colour_random, and colour_blend blocks.
  // Unit tests may attempt to install multiple times, which is currently unsafe.
  try {
    installFieldColourBlocks({javascript: javascriptGenerator});
  } catch (e) {
    console.warn(e);
  }

  // Custom fields need to be registered for two reasons:
  // 1. To be used in JSON block definitions, e.g. in Music Lab
  // 2. To be used by core Blockly blocks to override default field functionality, e.g. colour blocks
  type registrableFieldType = BlocklyCore.fieldRegistry.RegistrableField;
  // elements in this list should be structured as follows:
  // [field registry name for field, class to use as override]
  const fieldsToRegister: [string, registrableFieldType][] = [
    // CdoFieldColour extends from a plugin. We know it's a field, so it's safe to cast as unknown.
    ['field_colour', CdoFieldColour as unknown as registrableFieldType],
    ['field_dropdown', CdoFieldDropdown],
    ['field_label', CdoFieldLabel],
    ['field_number', CdoFieldNumber],
    ['field_parameter', CdoFieldParameter],
    ['field_variable', CdoFieldVariable],
  ];
  // Tell Blockly to use our custom versions of fields
  fieldsToRegister.forEach(override => {
    const fieldRegistryName = override[0];
    const fieldClass = override[1];
    try {
      blocklyWrapper.fieldRegistry.unregister(fieldRegistryName);
    } catch (_e) {
      // Not yet registered; safe to skip unregister.
    }
    blocklyWrapper.fieldRegistry.register(fieldRegistryName, fieldClass);
  });

  // In Blockly v12.4+, translate() sets style.transform (CSS) on the block canvas via
  // LayerManager instead of the SVG transform attribute. This breaks
  // @blockly/plugin-scroll-options, which reads svgBlockCanvas_.getAttribute('transform').
  // Fix: mirror the full (x,y) translation to the SVG attribute and clear the CSS style
  // to avoid double-counting in getRelativeXY(), which reads both.
  // Guard against re-wrapping when initializeBlocklyWrapper() is called multiple times.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(blocklyWrapper.WorkspaceSvg.prototype.translate as any).__cdoPatched) {
    const originalTranslate = blocklyWrapper.WorkspaceSvg.prototype.translate;
    blocklyWrapper.WorkspaceSvg.prototype.translate = function (
      x: number,
      y: number
    ) {
      originalTranslate.call(this, x, y);
      if (this.svgBlockCanvas_) {
        // Set the SVG attribute with the full translation (x, y already include
        // absoluteLeft/absoluteTop) so getInjectionDivXY reads the correct origin offset.
        this.svgBlockCanvas_.setAttribute(
          'transform',
          `translate(${x},${y}) scale(${this.scale})`
        );
        // Remove the CSS style.transform so getRelativeXY() doesn't double-count:
        // it reads both the SVG attribute and the CSS style, so we must clear one.
        this.svgBlockCanvas_.style.transform = '';
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (blocklyWrapper.WorkspaceSvg.prototype.translate as any).__cdoPatched =
      true;
  }

  // TODO: Can/should we make CdoTrashcan have the same type as Trashcan?
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  blocklyWrapper.Trashcan = CdoTrashcan as any;

  blocklyWrapper.registry.register(
    blocklyWrapper.registry.Type.FLYOUTS_VERTICAL_TOOLBOX,
    blocklyWrapper.registry.DEFAULT,
    CdoVerticalFlyout,
    true /* opt_allowOverrides */
  );

  blocklyWrapper.registry.register(
    blocklyWrapper.registry.Type.RENDERER,
    Renderers.GERAS,
    CdoRendererGeras,
    true /* opt_allowOverrides */
  );
  blocklyWrapper.registry.register(
    blocklyWrapper.registry.Type.RENDERER,
    Renderers.THRASOS,
    CdoRendererThrasos,
    true /* opt_allowOverrides */
  );
  blocklyWrapper.registry.register(
    blocklyWrapper.registry.Type.RENDERER,
    Renderers.ZELOS,
    CdoRendererZelos,
    true /* opt_allowOverrides */
  );
  blocklyWrapper.registry.register(
    blocklyWrapper.registry.Type.CONNECTION_CHECKER,
    'cdo_connection_checker',
    CdoConnectionChecker,
    true /* opt_allowOverrides */
  );
  try {
    blocklyWrapper.serialization.registry.unregister('blocks');
  } catch (_e) {
    // Not yet registered; safe to skip unregister.
  }
  blocklyWrapper.serialization.registry.register(
    'blocks',
    new CdoBlockSerializer()
  );

  const procedureSerializer =
    new blocklyWrapper.serialization.procedures.ProcedureSerializer(
      ObservableProcedureModel,
      ObservableParameterModel
    );

  blocklyWrapper.procedureSerializer = procedureSerializer;
  // Register the shareable procedures serializer, used for the modal function editor.
  try {
    blocklyWrapper.serialization.registry.unregister('procedures');
  } catch (_e) {
    // Not yet registered; safe to skip unregister.
  }
  blocklyWrapper.serialization.registry.register(
    'procedures',
    procedureSerializer
  );

  registerAllContextMenuItems();

  // This is wrapping a settable property, but we can't use wrapSettableProperty
  // because the alias name is not the same as the underlying property name.
  Object.defineProperty(blocklyWrapper, 'mainBlockSpace', {
    get: function () {
      return this.mainWorkspace || this.blockly_.getMainWorkspace();
    },
    // Setter is only used in tests.
    set: function (workspace) {
      this.mainWorkspace = workspace;
    },
  });
  // These are also wrapping read only properties, but can't use wrapReadOnlyProperty
  // because the alias name is not the same as the underlying property name.
  Object.defineProperty(blocklyWrapper, 'mainBlockSpaceEditor', {
    get: function () {
      return this.mainWorkspace || this.blockly_.getMainWorkspace();
    },
  });
  Object.defineProperty(blocklyWrapper, 'selected', {
    get: function () {
      // In the event that the block is no longer focused, we can
      // potentially use the workspace's focused node.
      // This allows `Blockly.selected` to work from the dev console.
      const focusedNode = this.FocusableTreeTraverser.findFocusedNode(
        // common.getMainWorkspace() gets the currently active workspace,
        // as opposed to the student's "primary" workspace.
        Blockly.common.getMainWorkspace()
      );
      if (focusedNode && Blockly.isSelectable(focusedNode)) {
        return focusedNode;
      }
      // If the focused node is not selectable, fall back to the
      // getSelected() method.
      return Blockly.getSelected();
    },
  });

  // Properties cannot be modified until wrapSettableProperty has been called
  SETTABLE_PROPERTIES.forEach(property =>
    blocklyWrapper.wrapSettableProperty(property)
  );

  // Allows for dynamically setting the workspace theme with workspace.setTheme()
  blocklyWrapper.themes = {
    [Themes.MODERN]: CdoTheme,
    [Themes.DARK]: CdoDarkTheme,
    [Themes.HIGH_CONTRAST]: CdoHighContrastTheme,
    [Themes.HIGH_CONTRAST_DARK]: CdoHighContrastDarkTheme,
    [Themes.JIGSAW]: CdoJigsawTheme,
    [Themes.PROTANOPIA]: CdoProtanopiaTheme,
    [Themes.PROTANOPIA_DARK]: CdoProtanopiaDarkTheme,
    [Themes.DEUTERANOPIA]: CdoDeuteranopiaTheme,
    [Themes.DEUTERANOPIA_DARK]: CdoDeuteranopiaDarkTheme,
    [Themes.TRITANOPIA]: CdoTritanopiaTheme,
    [Themes.TRITANOPIA_DARK]: CdoTritanopiaDarkTheme,
  };

  // Assign all of the properties of the javascript generator to the forBlock array
  // Prevents deprecation warnings related to https://github.com/google/blockly/pull/7150
  Object.setPrototypeOf(javascriptGenerator.forBlock, javascriptGenerator);

  blocklyWrapper.JavaScript = javascriptGenerator;

  const extendedWorkspaceSvg = blocklyWrapper.WorkspaceSvg
    .prototype as ExtendedWorkspaceSvg;

  // Used in levels when starting over or resetting Version History
  const googleBlocklyBlocklyClear = blocklyWrapper.WorkspaceSvg.prototype.clear;
  extendedWorkspaceSvg.clear = function () {
    googleBlocklyBlocklyClear.call(this);
    // After clearing the workspace, we need to reinitialize global variables
    // if there are any.
    if (this.globalVariables) {
      const variableMap = this.getVariableMap();
      this.globalVariables.forEach(varName => {
        variableMap.createVariable(varName);
      });
    }
  };

  // Used in levels with pre-defined "Blockly Variables"
  extendedWorkspaceSvg.registerGlobalVariables = function (variableList) {
    this.globalVariables = variableList;
    const variableMap = this.getVariableMap();
    variableList.forEach(varName => {
      variableMap.createVariable(varName);
    });
  };

  gestureOverrides(blocklyWrapper);

  blocklyWrapper.getGenerator = function () {
    // Additional methods are added to the generator when initializeGenerator is called,
    // So it is safe to cast as unknown here.
    return this.JavaScript as unknown as ExtendedJavascriptGenerator;
  };

  blocklyWrapper.inputTypes = blocklyInstance.inputs.inputTypes;

  blocklyWrapper.addEmbeddedWorkspace = function (workspace) {
    this.embeddedWorkspaces.push(workspace.id);
  };

  blocklyWrapper.isEmbeddedWorkspace = function (workspace) {
    return this.embeddedWorkspaces.includes(workspace.id);
  };

  blocklyWrapper.findEmptyContainerBlock = function (blocks) {
    for (const block of blocks) {
      const emptyInput = block.inputList.find(
        input =>
          input.type === blocklyWrapper.inputTypes.STATEMENT &&
          input.connection?.targetConnection === null
      );
      if (emptyInput) {
        return block;
      }
    }
    return null;
  };

  blocklyWrapper.BlockSpace = {
    EVENTS: WORKSPACE_EVENTS,
    onMainBlockSpaceCreated: callback => {
      if (Blockly.mainBlockSpace) {
        callback();
      } else {
        document.addEventListener(
          Blockly.BlockSpace.EVENTS.MAIN_BLOCK_SPACE_CREATED,
          callback
        );
      }
    },
  };

  // An embedded workspace is one that only displays blocks, but never
  // runs them. It is used for things like hint blocks, blocks in instructions,
  // and previewing blocks for levelbuilders.
  // We used to refer to these as "readOnlyBlockSpaces", which was confusing with normal,
  // read only workspaces.
  blocklyWrapper.createEmbeddedWorkspace = function (
    container,
    xml,
    options = {}
  ) {
    const theme = options.theme || CdoTheme;
    const workspace = new Blockly.WorkspaceSvg({
      readOnly: true,
      theme,
      plugins: {},
      RTL: options.rtl,
      renderer: options.renderer || Renderers.DEFAULT,
    } as BlocklyCore.Options);
    // Track that this is and embedded workspace to avoid trying
    // to run logic on it to ensure things run properly (such as procedures).
    blocklyWrapper.addEmbeddedWorkspace(workspace);
    const svg = Blockly.utils.dom.createSvgElement(
      'svg',
      {
        xmlns: 'http://www.w3.org/2000/svg',
        'xmlns:html': 'http://www.w3.org/1999/xhtml',
        'xmlns:xlink': 'http://www.w3.org/1999/xlink',
        class: `${Renderers.DEFAULT}-renderer modern-theme readOnlyBlockSpace injectionDiv`,
      },
      null
    );

    // Core Blockly requires a container div to be LTR, regardless of page direction.
    container.setAttribute('dir', 'LTR');
    container.style.display = 'inline-block';
    container.appendChild(svg);
    svg.appendChild(workspace.createDom());

    workspace.setTheme(theme as BlocklyCore.Theme);
    // We do not include hidden definitions in embedded workspaces
    // because embedded workspaces are only used for displaying blocks.
    const includeHiddenDefinitions = false;
    loadBlocksToWorkspace(
      workspace,
      Blockly.Xml.domToText(xml),
      includeHiddenDefinitions
    );

    // Loop through all the parent blocks and remove vertical translation value
    // This makes the output more condensed and readable, while preserving
    // horizontal translation values for RTL rendering.
    const blocksInWorkspace = workspace.getAllBlocks();
    blocksInWorkspace
      .filter(block => block.getParent() === null)
      .forEach(block => {
        const svgTransformList = block.getSvgRoot().transform.baseVal;
        const svgTransform = svgTransformList.getItem(0);
        const svgTranslationX = svgTransform.matrix.e;
        svgTransform.setTranslate(svgTranslationX, 0);
        block.render();
      });

    // Shrink SVG to size of the block
    const bbox = (svg as SVGGraphicsElement).getBBox();
    svg.setAttribute('height', `${bbox.height + bbox.y}`);
    svg.setAttribute('width', `${bbox.width + bbox.x}`);
    return workspace;
  };

  blocklyWrapper.inject = function (container, opt_options) {
    // Ensure we do not translate content within the blockly workspace
    if (typeof container !== 'string') {
      (container as HTMLElement).classList.add('notranslate');
    }

    // Set the default value for hasLoadedBlocks to false.
    blocklyWrapper.hasLoadedBlocks = false;
    if (!opt_options) {
      opt_options = {};
    }
    // We override inject and have extra options we pass in, so we cast to utilize those options here.
    const optOptionsExtended = opt_options as ExtendedBlocklyOptions;
    blocklyWrapper.isJigsaw = optOptionsExtended.isJigsaw;
    const options = {
      ...optOptionsExtended,
      theme: optOptionsExtended.theme || CdoTheme,
      trashcan: false, // Don't use default trashcan.
      move: {
        wheel: true,
        drag: true,
        scrollbars: {
          horizontal: true,
          vertical: true,
        },
      },
      plugins: {
        blockDragger: CdoScrollBlockDragger,
        metricsManager: CdoMetricsManager,
        connectionChecker: CdoConnectionChecker,
      },
      renderer: optOptionsExtended.renderer || Renderers.DEFAULT,
      comments: !!optOptionsExtended.comments,
      media: '/blockly/media/google_blockly',
      modalInputs: false, // Prevents pop-up editor on mobile
    };
    // For old levels, we remove statically-defined procedure call blocks from the
    // auto-populated Functions category. We also remove any invisible blocks because
    // modern Blockly doesn't support them.
    if (typeof options.toolbox === 'string') {
      const toolboxDom = Blockly.Xml.textToDom(options.toolbox);
      const visibleBlocksDom = removeInvisibleBlocks(toolboxDom);
      const finalToolboxDom = removeStaticCallBlocks(visibleBlocksDom);
      options.toolbox = Blockly.Xml.domToText(finalToolboxDom);
    }
    // CDO Blockly takes assetUrl as an inject option, and it's used throughout
    // apps, so we should also set it here.
    blocklyWrapper.assetUrl =
      optOptionsExtended.assetUrl || ((path: string) => `./${path}`);

    // CDO Blockly takes customSimpleDialog as an inject option and uses it
    // instead of the default prompt dialogs, so we should also set it here.
    blocklyWrapper.customSimpleDialog = optOptionsExtended.customSimpleDialog;

    // In order to prevent writing duplicate solution entries to the level_sources table,
    // we strip block ids from XML when saving. An exception is made for block ids that
    // are explicitly set in the level's toolbox or start blocks.
    blocklyWrapper.levelBlockIds =
      optOptionsExtended.levelBlockIds || new Set<string>();

    // Shrink container to make room for the workspace header
    if (!optOptionsExtended.isBlockEditMode) {
      (
        container as HTMLElement
      ).style.height = `calc(100% - ${styleConstants['workspace-headers-height']}px)`;
    }
    blocklyWrapper.disableVariableEditing =
      !!optOptionsExtended.disableVariableEditing;
    blocklyWrapper.varsInGlobals = !!optOptionsExtended.varsInGlobals;
    blocklyWrapper.isStartMode =
      !!optOptionsExtended.editBlocks &&
      // Lab2 levels such as Music Lab use 'start_sources', while older labs use 'start_blocks'.
      [START_BLOCKS, START_SOURCES].includes(optOptionsExtended.editBlocks);
    blocklyWrapper.isToolboxMode =
      optOptionsExtended.editBlocks === 'toolbox_blocks';
    blocklyWrapper.analyticsData = optOptionsExtended.analyticsData;
    blocklyWrapper.toolboxBlocks = options.toolbox;
    blocklyWrapper.showUnusedBlocks = options.showUnusedBlocks;
    blocklyWrapper.blockLimitMap = createBlockLimitMap();
    blocklyWrapper.isDarkTheme = isDarkTheme(
      options.theme as BlocklyCore.Theme | undefined
    );

    // Only allow toggling disabled blocks in start mode.
    // This is also important for ensuring that Blockly does not
    // mistakenly keep orphaned blocks disabled when they are connected.
    options.disable = blocklyWrapper.isStartMode;

    const workspace = blocklyWrapper.blockly_.inject(
      container,
      options
    ) as ExtendedWorkspaceSvg;
    // Mark the blockly container as something we do not want translated
    // and undo the container being marked as such
    const div = workspace.getInjectionDiv();
    if (div) {
      div.classList.add('notranslate');
    }
    if (typeof container !== 'string') {
      (container as HTMLElement).classList.remove('notranslate');
    }

    getUserTheme(workspace.getTheme()).then((theme: BlocklyCore.Theme) => {
      setThemeAndRenderBlocks(
        workspace,
        theme,
        options.theme as BlocklyCore.Theme
      );
    });
    workspace.defs = BlocklyCore.utils.dom.createSvgElement(
      'defs',
      {id: 'blocklySvgDefs'},
      workspace.svgGroup_
    );

    if (!!options.grayOutUndeletableBlocks) {
      workspace.addChangeListener(handleGrayUndeletableBlocks);
    }

    blocklyWrapper.topLevelProcedureAutopopulate =
      !!options.topLevelProcedureAutopopulate;
    blocklyWrapper.showBlockHelp = !!optOptionsExtended.showBlockHelp;

    if (options.noFunctionBlockFrame) {
      workspace.noFunctionBlockFrame = options.noFunctionBlockFrame;
    }

    // Typically, we need to handle disabling blocks that are not connected to an
    // appropriate top block. A few exceptions exist.
    if (
      // Blocks should not be disabled when editing a toolbox.
      !blocklyWrapper.isToolboxMode &&
      // When editing blocks in block pools, we do not need to disable them.
      !optOptionsExtended.isBlockEditMode &&
      // Jigsaw blocks are never disabled.
      !blocklyWrapper.isJigsaw
    ) {
      workspace.addChangeListener(disableOrphans);
    }
    if (blocklyWrapper.blockLimitMap && blocklyWrapper.blockLimitMap.size > 0) {
      workspace.addChangeListener(updateBlockLimits);
    }

    // In toolbox mode, automatically clean up the workspace as blocks are moved.
    // This needs to use Blockly's built-in cleanUp method, which arranges
    // all blocks into a single column.
    if (blocklyWrapper.isToolboxMode) {
      workspace.addChangeListener(event => {
        if (event.type === BlocklyCore.Events.MOVE) {
          workspace.cleanUp();
        }
      });
    } else {
      // Outside of toolbox mode, we need use a custom workspace cleanUp method.
      // Our version prevents blocks from overlapping, moving the blocks as
      // minimally as possible.
      // This command is accessible via the workspace context menu.
      extendedWorkspaceSvg.cleanUp = function (
        includeImmovableBlocks?: boolean
      ) {
        cleanUp(this, includeImmovableBlocks);
      };
    }
    // When either the main workspace or the toolbox workspace viewport
    // changes, adjust any callouts so they stay pointing to the appropriate
    // location.
    workspace.addChangeListener(adjustCalloutsOnViewportChange);
    workspace
      .getFlyout()
      ?.getWorkspace()
      ?.addChangeListener(adjustCalloutsOnViewportChange);

    // We store the workspace width for RTL workspaces so that we can move
    // blocks back to the correct positions after a browser window resize.
    // See: https://github.com/google/blockly/issues/8637
    workspace.addChangeListener(storeWorkspaceWidth);
    // Jigsaw blocks have additional path SVGs that need to be filled with
    // a pattern image.
    if (optOptionsExtended.isJigsaw) {
      workspace.addChangeListener(setPathFill);
    }
    window.addEventListener('resize', bumpRTLBlocks);

    initializeScrollbarPair(workspace);

    window.addEventListener('resize', reflowToolbox);

    document.dispatchEvent(
      utils.createEvent(Blockly.BlockSpace.EVENTS.MAIN_BLOCK_SPACE_CREATED)
    );

    const scrollOptionsPlugin = new ScrollOptions(workspace);
    scrollOptionsPlugin.init({
      // Rather than using the block edge, we always use the mouse cursor (plus a margin)
      // to activate block-based scrolling.
      edgeScrollOptions: {oversizeBlockThreshold: 0},
    });

    const trashcan = new CdoTrashcan(workspace);
    trashcan.init();

    blocklyWrapper.setMainWorkspace(workspace);

    if (optOptionsExtended.useBlocklyDynamicCategories) {
      const originalVariableFlyoutCategory =
        workspace.getToolboxCategoryCallback('VARIABLE');
      if (originalVariableFlyoutCategory) {
        workspace.registerToolboxCategoryCallback('VARIABLE', workspace =>
          filterFunctionArgVariables(
            workspace,
            originalVariableFlyoutCategory(workspace)
          )
        );
      }
    } else {
      // Same flyout callbacks are used for both main workspace categories
      // and categories when modal function editor is enabled.
      workspace.registerToolboxCategoryCallback(
        'PROCEDURE',
        functionsFlyoutCategory
      );
      workspace.registerToolboxCategoryCallback(
        'VARIABLE',
        variablesFlyoutCategory
      );
    }

    workspace.registerToolboxCategoryCallback(
      'Behavior',
      behaviorsFlyoutCategory
    );

    // Hidden workspace where we can put function definitions.
    const hiddenDefinitionWorkspace =
      new Blockly.Workspace() as ExtendedWorkspace;
    hiddenDefinitionWorkspace.addChangeListener(disableOrphans);
    // The hidden definition workspace is not rendered, so do not try to add
    // svg frames around the definitions.
    hiddenDefinitionWorkspace.noFunctionBlockFrame = true;
    blocklyWrapper.setHiddenDefinitionWorkspace(hiddenDefinitionWorkspace);
    blocklyWrapper.useModalFunctionEditor = options.useModalFunctionEditor;
    // Disable parameter editing by default (e.g. Lab2)
    blocklyWrapper.enableParamEditing = options.disableParamEditing === false;

    if (options.useModalFunctionEditor) {
      // If the modal function editor is enabled for this level,
      // initialize the modal function editor.
      blocklyWrapper.functionEditor = new FunctionEditor();
      blocklyWrapper.functionEditor.init(options);
    }

    // Set up variable localization
    initializeVariableLocalization(workspace);

    return workspace;
  };

  blocklyWrapper.setMainWorkspace = function (mainWorkspace) {
    this.mainWorkspace = mainWorkspace;
  };

  blocklyWrapper.setHiddenDefinitionWorkspace = function (
    hiddenDefinitionWorkspace
  ) {
    this.hiddenDefinitionWorkspace = hiddenDefinitionWorkspace;
  };

  blocklyWrapper.getHiddenDefinitionWorkspace = function () {
    return this.hiddenDefinitionWorkspace;
  };

  blocklyWrapper.getMainWorkspace = function () {
    return blocklyWrapper.mainBlockSpace;
  };

  blocklyWrapper.getFunctionEditorWorkspace = function () {
    return blocklyWrapper.functionEditor?.getWorkspace();
  };

  // Initialize metadata houses for original English source strings for
  // various Blockly metadata that gets installed. These are used by the
  // updateLocale(), localizeVariables(), etc, functions to translate a
  // variety of both custom and built-in Blockly content.
  blocklyWrapper.SourceMsg = {};
  blocklyWrapper.SourceVariables = {};
  blocklyWrapper.SourceCustomBlocks = {
    blockDefinitionsByName: {},
    blockTexts: {},
  };
  blocklyWrapper.SourceCustomInputTypes = {};

  initializeBlocklyXml(blocklyWrapper);
  initializeGenerator(blocklyWrapper);
  initializeVariables(blocklyWrapper);
  initializeCdoConstants(blocklyWrapper);
  initializeCss(blocklyWrapper);

  blocklyWrapper.Blocks.unknown = UNKNOWN_BLOCK;
  blocklyWrapper.JavaScript.forBlock.unknown = () => '/* unknown block */\n';
  blocklyWrapper.getPointerBlockImageUrl = getPointerBlockImageUrl;

  return blocklyWrapper;
}

module.exports = initializeBlocklyWrapper;
